import React from 'react';
import PropTypes from 'prop-types';
import UnreachableMark from 'common/OnboardingComponents/UnreachableMark';
import { localTimeString, humanize, getRelativeDuration } from 'common/helpers';
import { MACHINE_STATE, MACHINE_CONDITION } from 'common/constants';
import DataTable, { NameLink, Dot, ConditionCell } from 'common/components/organisms/DataTable';
import { getHSColor } from '../utils';

const CONDITION_META = MACHINE_CONDITION.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

/**
 * Assets list table. A thin, asset-specific configuration of the shared
 * DataTable: it maps the machine `headers` prop into declarative columns with
 * bespoke cell renderers, and preserves the exact data-testid contract the
 * Assets cucumber suite depends on (asset-table-row, asset-name-cell,
 * asset-sort-header-<name>, asset-empty-state).
 */
class AssetsTable extends React.Component {
  renderCellContent = (header, item) => {
    switch (header.name) {
      case 'name':
        return (
          <NameLink
            to={`/machines/${item.id}`}
            onClick={e => e.stopPropagation()}
            data-testid="asset-name-cell"
            data-asset-name={item.name}
          >
            {item.has_unreachable_sensors && <UnreachableMark marginRight="7px" />}
            {item.name}
          </NameLink>
        );
      case 'condition': {
        const meta = CONDITION_META[item.condition];
        return (
          <ConditionCell>
            <Dot color={meta ? meta.color : null} />
            {item.condition ? humanize(item.condition) : '--'}
          </ConditionCell>
        );
      }
      case 'area_name':
        return item.area_path || '--';
      case 'current_state':
        return MACHINE_STATE[item.current_state] || 'NA';
      case 'health_score':
        return item.health_score !== null && item.health_score !== undefined
          ? `${item.health_score}` : '--';
      case 'last_measurement_time':
        return item.last_measurement_time ? localTimeString(item.last_measurement_time) : '--';
      case 'setup_completion_percent':
        return item.setup_completion_percent != null ? `${item.setup_completion_percent} %` : '--';
      case 'condition_since':
        return item.condition_since ? getRelativeDuration(item.condition_since) : '--';
      default:
        return '--';
    }
  };

  cellColor = (header, item) => {
    if (header.name !== 'health_score') return undefined;
    return getHSColor(item.health_score, this.props.conditionLevels);
  };

  buildColumns = () => this.props.headers.map(header => ({
    name: header.name,
    label: header.label,
    alignCenter: header.alignCenter,
    noSorter: header.noSorter,
    render: item => this.renderCellContent(header, item),
    cellColor: item => this.cellColor(header, item)
  }));

  render() {
    const {
      items, sorterState, setSorter, onMachineSelect,
      loading, hasMore, loadMoreItems, emptyMsg
    } = this.props;
    const rows = items && items.object ? items.object : [];

    return (
      <DataTable
        columns={this.buildColumns()}
        rows={rows}
        onRowClick={item => onMachineSelect(item.id)}
        rowAttrs={item => ({ 'data-asset-name': item.name })}
        rowTestId="asset-table-row"
        sortHeaderTestIdPrefix="asset-sort-header-"
        emptyTestId="asset-empty-state"
        sort={sorterState ? sorterState.sorter : null}
        onSortChange={sorterState ? setSorter : null}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMoreItems}
        emptyMessage={emptyMsg}
      />
    );
  }
}

AssetsTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.object).isRequired,
  items: PropTypes.object,
  sorterState: PropTypes.object,
  setSorter: PropTypes.func.isRequired,
  onMachineSelect: PropTypes.func.isRequired,
  conditionLevels: PropTypes.object,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  loadMoreItems: PropTypes.func,
  emptyMsg: PropTypes.string
};

AssetsTable.defaultProps = {
  items: { object: [] },
  sorterState: null,
  conditionLevels: null,
  loading: false,
  hasMore: false,
  loadMoreItems: () => {},
  emptyMsg: 'No results found'
};

export default AssetsTable;
